import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonColor, ButtonVariant } from "@swivl-mono/ui";
import { FiCheck, FiMinus } from "react-icons/fi";

// A step either got data (completed) or was advanced past empty (skipped).
export type StepOutcome = "completed" | "skipped";

interface WizardStepShellProps {
  children: React.ReactNode;
  footer: React.ReactNode;
}

// Standard step layout: a scrollable body above a pinned footer.
export function WizardStepShell({ children, footer }: WizardStepShellProps): React.ReactElement {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-1 pt-3">{children}</div>
      {footer}
    </div>
  );
}

interface WizardProgressProps {
  labels: string[];
  current: number;
  // Label index -> how it resolved; drives the icon (check/dash) independent of position.
  outcomes: Map<number, StepOutcome>;
  // Jump to a step (label index); the first pip (Details) is never selectable.
  onSelect?: (index: number) => void;
}

// Step rail: current = orange, completed = teal check, skipped = grey dash. Steps are clickable.
export function WizardProgress({ labels, current, outcomes, onSelect }: WizardProgressProps): React.ReactElement {
  return (
    <div className="flex items-center gap-1 border-b border-grey-200 px-1 pb-3">
      {labels.map((label, index) => {
        const active = index === current;
        const outcome = outcomes.get(index);
        const clickable = !!onSelect && index > 0;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!clickable}
              onClick={clickable ? () => onSelect?.(index) : undefined}
              className={filterClasses("flex items-center gap-1.5", clickable && "cursor-pointer hover:opacity-80")}
            >
              <span
                className={filterClasses(
                  "flex size-5 items-center justify-center rounded-full text-xs font-semibold",
                  active
                    ? "bg-orange-500 text-white"
                    : outcome === "completed"
                      ? "bg-teal-100 text-teal-800"
                      : outcome === "skipped"
                        ? "bg-grey-100 text-grey-500"
                        : "bg-grey-200 text-grey-500"
                )}
              >
                {active ? (
                  index + 1
                ) : outcome === "completed" ? (
                  <FiCheck className="size-3" />
                ) : outcome === "skipped" ? (
                  <FiMinus className="size-3" />
                ) : (
                  index + 1
                )}
              </span>
              <span className={filterClasses("body-xs", active ? "font-semibold text-grey-900" : "text-grey-500")}>{label}</span>
            </button>
            {index < labels.length - 1 && <span className="mx-1 h-px w-4 bg-grey-200" />}
          </div>
        );
      })}
    </div>
  );
}

interface WizardFooterProps {
  onBack?: () => void;
  onClose: () => void;
  onSkip: () => void;
  onApprove: () => void;
  saving?: boolean;
}

// One footer for every wizard step: Save & close, Back, Skip for now, Approve.
// Back is disabled on the first step (nothing precedes it) so the button set stays uniform.
export function WizardFooter({ onBack, onClose, onSkip, onApprove, saving }: WizardFooterProps): React.ReactElement {
  return (
    <div className="mt-4 flex items-center justify-between border-t border-grey-200 pt-3">
      <div className="flex items-center gap-2">
        <Button variant={ButtonVariant.GHOST} onClick={onClose}>
          Save &amp; close
        </Button>
        <Button variant={ButtonVariant.GHOST} disabled={!onBack} onClick={onBack}>
          Back
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button variant={ButtonVariant.GHOST} onClick={onSkip}>
          Skip for now
        </Button>
        <Button color={ButtonColor.ORANGE} loading={saving} onClick={onApprove}>
          Approve
        </Button>
      </div>
    </div>
  );
}
